import React, { useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';

import { Autocomplete, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import debounce from 'lodash/debounce';
import axios from 'utils/axios-rails';

const propTypes = {
  queryURL: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  selected: PropTypes.array.isRequired,

  label: PropTypes.string,
  placeholder: PropTypes.string,

  extraParams: PropTypes.object, // for query
  textFieldProps: PropTypes.object,
}

const defaultProps = {
  label: "Busqueda",
  placeholder: "some@email.com",
  extraParams: {},
  textFieldProps: {},
}

const SearchAutocomplete = forwardRef( (props, ref) => {

  const [text, setText] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const onTextChange = (value) => {
    setText(value);
    debouncedQueryOptions(value);

  }

  // Polaris' Autocomplete handed `onSelect` an array of selected values, and the
  // consuming forms read it as such (TaskForm does `ids[0]`), so the array is
  // rebuilt here rather than passing the bare value.
  const onSelect = (item) => {
    if(!item) return;

    props.onSelect([item.value], item)
  }

  const queryOptions = async (value) => {
    setLoading(true);
    try {
      const response = await axios.post(props.queryURL, {
        query: {
          value,
          ...props.extraParams
        }
      })

      if(response.data.options){
        setOptions( response.data.options );
      }
    } catch (error) {
      console.log("Boom", error)
    }
    setLoading(false);
  }

  const debouncedQueryOptions = useCallback( debounce(queryOptions, 700),[props.extraParams]);

  // List of functions to expose on parent
  useImperativeHandle(ref, () => ({
    clearOptions: () => setOptions([]), // clear options from parent
  }));


  return (
    <Autocomplete
      fullWidth
      // The backend already filters on every keystroke, so the built-in
      // client-side filter would only hide freshly fetched matches.
      filterOptions={(availableOptions) => availableOptions}
      options={ options }
      getOptionLabel={ (option) => option.label ?? "" }
      isOptionEqualToValue={ (option, value) => option.value === value.value }
      inputValue={ text }
      onInputChange={ (event, value) => onTextChange(value) }
      onChange={ (event, item) => onSelect(item) }
      loading={ loading }
      value={ null }
      renderInput={ (params) => (
        <TextField
          {...params}
          label={ props.label }
          placeholder={ props.placeholder }
          InputLabelProps={{ shrink: true }}
          {...props.textFieldProps}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="disabled" />
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  )
});

SearchAutocomplete.propTypes = propTypes;
SearchAutocomplete.defaultProps = defaultProps;

export default SearchAutocomplete;
